import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import axios from 'axios';
import { z } from 'zod';
import { eoiFormSchema } from '@court-sync/shared';
import type { Student } from '@court-sync/shared';
import { useAuthStore } from '../../../../store/auth';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { appendPublicBookingWizardContextQueries, publicBookingStepPath } from '../bookingPaths';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useAcademyPaths } from '../../../../hooks/useAcademyPaths';
import { eoiService } from '../../../../services/eoi';
import { studentService } from '../../../../services/student';
import { Button, Field, FieldError, FieldLabel, Textarea } from '@/components/ui';
import { HeartHandshake } from 'lucide-react';

const eoiWithStudentSchema = eoiFormSchema.extend({
  studentId: z.string().uuid('Select a student'),
});

type EoiFormValues = z.infer<typeof eoiWithStudentSchema>;

export function BookingEOIStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { user: authStoreUser } = useAuthStore();
  const academy = usePublicBookingStore((s) => s.academy);
  const { p } = useAcademyPaths(academy);
  const lesson = usePublicBookingStore((s) => s.lesson);
  const userType = usePublicBookingStore((s) => s.userType);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const [students, setStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EoiFormValues>({
    resolver: zodResolver(eoiWithStudentSchema),
    defaultValues: {
      experienceLevel: 'beginner',
      goals: '',
      availability: '',
      message: '',
      studentId: '',
    },
  });

  useEffect(() => {
    if (!userType || !authStoreUser) {
      navigate(
        appendPublicBookingWizardContextQueries(
          publicBookingStepPath(academySlug!, lessonId!, 'register'),
          { invitationToken, manualInviteToken }
        ),
        { replace: true }
      );
    }
  }, [
    userType,
    authStoreUser,
    academySlug,
    lessonId,
    navigate,
    invitationToken,
    manualInviteToken,
  ]);

  useEffect(() => {
    if (!academy?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingStudents(true);
        const res = await studentService.getStudents(academy.id);
        const list = res.data || [];
        if (!cancelled) setStudents(list);
      } catch {
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [academy?.id]);

  const onSubmit = async (data: EoiFormValues) => {
    if (!academy?.id || !lessonId) return;
    try {
      const res = await eoiService.submitEOI({
        academyId: academy.id,
        lessonId,
        studentId: data.studentId,
        experienceLevel: data.experienceLevel,
        goals: data.goals || undefined,
        availability: data.availability || undefined,
        message: data.message || undefined,
      });
      if (res.success) {
        setSubmitted(true);
        toast.success('Your expression of interest has been submitted.');
      } else {
        toast.error(res.error || 'Submission failed');
      }
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object' && 'error' in e.response.data
          ? String((e.response.data as { error?: string }).error)
          : e instanceof Error
            ? e.message
            : 'Submission failed';
      toast.error(msg);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto px-4 py-10 text-center space-y-4">
        <HeartHandshake className="w-14 h-14 mx-auto text-indigo-600" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Thank you</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Your expression of interest has been submitted. The academy will review your application and contact you
          about an assessment. You will receive a confirmation email shortly.
        </p>
        <Button type="button" variant="outline" onClick={() => navigate(p?.('') ?? '/')}>
          Back to academy
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <div className="flex items-center gap-2 mb-6">
        <HeartHandshake className="w-8 h-8 text-indigo-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Expression of interest</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {lesson?.name ? `${lesson.name} — ` : ''}
            This program uses invitations. Tell us about the player and we&apos;ll be in touch.
          </p>
        </div>
      </div>

      {loadingStudents ? (
        <p className="text-gray-500 text-center py-8">Loading…</p>
      ) : students.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-200">
          Add a student profile first to submit an expression of interest. Continue to student details.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Field data-invalid={!!errors.studentId}>
            <FieldLabel htmlFor="eoi-student">Student *</FieldLabel>
            <select id="eoi-student" className="input w-full" {...register('studentId')}>
              <option value="">Select…</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                  {s.isSelf ? ' (you)' : ''}
                </option>
              ))}
            </select>
            <FieldError>{errors.studentId?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.experienceLevel}>
            <FieldLabel htmlFor="eoi-exp">Experience level *</FieldLabel>
            <select id="eoi-exp" className="input w-full" {...register('experienceLevel')}>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            <FieldError>{errors.experienceLevel?.message}</FieldError>
          </Field>

          <Field>
            <FieldLabel htmlFor="eoi-goals">Goals</FieldLabel>
            <Textarea id="eoi-goals" rows={3} placeholder="What would you like to achieve?" {...register('goals')} />
          </Field>

          <Field>
            <FieldLabel htmlFor="eoi-avail">Availability</FieldLabel>
            <Textarea
              id="eoi-avail"
              rows={2}
              placeholder="Preferred days or times"
              {...register('availability')}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="eoi-msg">Message</FieldLabel>
            <Textarea id="eoi-msg" rows={3} placeholder="Anything else we should know" {...register('message')} />
          </Field>

          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? 'Submitting…' : 'Submit expression of interest'}
          </Button>
        </form>
      )}

      {!loadingStudents && students.length === 0 && (
        <div className="mt-6">
          <Button
            type="button"
            onClick={() =>
              navigate(
                appendPublicBookingWizardContextQueries(
                  publicBookingStepPath(academySlug!, lessonId!, 'details'),
                  { invitationToken, manualInviteToken }
                )
              )
            }
          >
            Continue to add student details
          </Button>
        </div>
      )}
    </div>
  );
}
